<template>
  <div
    :id="id"
    class="element shape frame draggable"
    :class="[cssClasses, { selected: isSelected, locked }]"
    :style="[basicStyles, transformStyles]"
  >
    <div
      class="shape-wrap"
      :class="[animationInStyle, animationOutStyle]"
    >
      <transition
        mode=""
        :enter-active-class="'animated ' + animationIn"
        :leave-active-class="'animated ' + animationOut"
        :duration="{ leave: leaveDuration }"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
      >
        <div
          v-show="toggle"
          class="background"
          :data-test-image-optimized="backgroundMetaOptimizeApplied"
          :class="loopingClass"
          :style="[
            animationStyles,
            borderRadiusStyles,
            backgroundStyles,
            borderStyles,
            boxShadowStyles,
            blurStyles,
          ]"
        >
          <iframe
            ref="iframeEl"
            :key="iframeKey"
            :src="iframeSrc"
            @load="onIframeLoad"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ElementsMixins from '../../mixins/elements-mixins';
import MoveMixins from '../../mixins/move-mixins';
import ResizeMixins from '../../mixins/resize-mixins';
import EventBus from '../../utils/event-bus';

const isValidUrl = (url) => {
  if (!url) {
    return false;
  }

  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

export default {
  name: 'FrameElement',
  mixins: [ElementsMixins, ResizeMixins, MoveMixins],

  data() {
    return {
      iframeSrc: '',
      pollData: null,
      frameLoaded: false,
      ready: false,
      messageListener: null,
      pendingDataUpdate: false,
    };
  },

  computed: {
    isSocialDisplay() {
      return this.element.isSocialDisplay === true;
    },

    isPollDisplay() {
      return (
        this.element?.isPollDisplay === true
        || this.element?.type === 'poll-display'
      );
    },

    url() {
      return this.element.url;
    },

    displayId() {
      return this.element.displayId;
    },

    pollId() {
      return this.element.pollId;
    },

    previewTagboardId() {
      return this.element.previewTagboardId || '396837';
    },

    postAnimation() {
      return this.element.postAnimation;
    },

    computedUrl() {
      // For poll displays, use the live-output endpoint
      if (this.isPollDisplay) {
        const PAGES_URL = process.env.PAGES_URL || 'https://pages.tagboard.com';
        const params = new URLSearchParams();
        params.set('disableScroll', 'true');
        return `${PAGES_URL}/display/live-output?${params.toString()}`;
      }

      // For social displays, build the display URL
      if (this.isSocialDisplay && this.displayId) {
        const DISPLAY_URL = process.env.DISPLAY_URL || 'https://display.tagboard.com';
        const tagboardId = this.previewTagboardId;

        // Add waitForMessage flag to signal iframe should wait for settings.
        // Hardcode `muted=true` so no audio plays within graphic editor,
        // since this is just a preview (double query param on opposing sides of the hash lol).
        // First `tagboardId` query param here doesn't do anything in live; it's only there to
        // trigger re-renders when updating the preview board
        // (live doesn't update based on route param properly).
        return `${DISPLAY_URL}/?waitForMessage=true&tagboardId=${tagboardId}#/tagboard/${tagboardId}/theme/${this.displayId}?muted=true`;
      }

      // For regular frames, use the url property
      return this.url;
    },

    iframeKey() {
      // Force iframe re-render when these values change
      if (this.isSocialDisplay) {
        return this.displayId;
      }

      return this.url;
    },
  },

  watch: {
    displayId() {
      // Only reload iframe when display changes
      this.ready = false;

      if (this.isPollDisplay) {
        this.fetchPollData();
      } else {
        this.updateIframeSrc();
      }
    },

    pollId() {
      if (this.isPollDisplay) {
        this.fetchPollData();
      } else {
        this.updateIframeSrc();
      }
    },

    'element.currentRoundIndex': function currentRoundIndex() {
      if (this.isPollDisplay && this.pollData) {
        this.sendPreviewData();
      }
    },

    'element.currentDisplayState': function currentDisplayState() {
      if (this.isPollDisplay && this.pollData) {
        this.sendPreviewData();
      }
    },

    'element.currentQuestionIndex': function currentQuestionIndex() {
      if (this.isPollDisplay && this.pollData) {
        this.sendPreviewData();
      }
    },

    previewTagboardId() {
      // Only reload iframe when tagboard changes
      this.ready = false;
      this.updateIframeSrc();
    },

    postAnimation() {
      // Send settings update via postMessage instead of reloading
      if (this.isSocialDisplay && this.ready) {
        this.sendSettingsToIframe();
      }
    },

    url: {
      immediate: true,
      handler() {
        // Debounce update iframeSrc,
        // so we aren't constantly reloading the frame when the URL is changing
        clearTimeout(this.updateTimeout);
        this.updateTimeout = setTimeout(() => {
          if (isValidUrl(this.url)) {
            this.iframeSrc = this.url;
          }
        }, 1e3);
      },
    },

    computedUrl: {
      immediate: true,
      handler() {
        if (!this.isPollDisplay) {
          this.updateIframeSrc();
        }
      },
    },
  },

  created() {
    // Fetch poll data if this is a poll display
    if (this.isPollDisplay && this.pollId && this.displayId) {
      this.fetchPollData();
    }
  },

  mounted() {
    EventBus.on('frame:refresh', this.handleRefresh);

    // Immediately set iframe src on mount for poll/social displays
    if (this.isPollDisplay) {
      const PAGES_URL = process.env.PAGES_URL || 'https://pages.tagboard.com';
      const params = new URLSearchParams();
      params.set('disableScroll', 'true');
      this.iframeSrc = `${PAGES_URL}/display/live-output?${params.toString()}`;

      // Listen for messages from the iframe
      this.messageListener = this.onMessage.bind(this);
      window.addEventListener('message', this.messageListener);
    } else if (this.isSocialDisplay) {
      this.iframeSrc = this.computedUrl;
      this.messageListener = (event) => {
        if (typeof event.data === 'string' && event.data === 'tgbLive:ready:true' && event.source === this.$refs.iframeEl?.contentWindow) {
          this.handleIframeReady();
        }
      };

      window.addEventListener('message', this.messageListener);
    }
  },

  beforeUnmount() {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }

    EventBus.off('frame:refresh', this.handleRefresh);
  },

  methods: {
    handleRefresh({ id }) {
      if (id === this.element.id) {
        const currentSrc = this.iframeSrc;
        this.iframeSrc = '';
        this.$nextTick(() => {
          this.iframeSrc = currentSrc;
        });
      }
    },

    onIframeLoad() {
      if (this.isPollDisplay) {
        this.frameLoaded = true;
        // Poll data should already be fetched in created/updated hooks
        // If we have pending data updates, send them now
        if (this.pollData && (this.pendingDataUpdate || this.pollId)) {
          this.sendPreviewData();
        }
      }
    },

    async fetchPollData() {
      if (!this.pollId || !this.displayId) {
        return;
      }

      try {
        const API_BASE = process.env.API_BASE || process.env.MONO_BASE;
        const response = await fetch(
          `${API_BASE}/public/polls/${this.pollId}?displayId=${this.displayId}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        this.pollData = await response.json();

        // Always try to send preview data after fetching
        // If iframe isn't ready, it will be queued
        this.sendPreviewData();
      } catch (error) {
        console.error('Error fetching poll data:', error);
      }
    },

    onMessage(e) {
      if (!e.data || !e.data.type) {
        return;
      }

      switch (e.data.type) {
        case 'display-preview-ready': {
          this.frameLoaded = true;
          this.sendPreviewData();
          break;
        }

        case 'display-update-received': {
          this.frameLoaded = true;
          break;
        }

        default:
          break;
      }
    },

    sendPreviewData() {
      if (!this.pollData || !this.$refs.iframeEl) {
        return;
      }

      // Wait for iframe to be ready before sending data
      if (!this.frameLoaded) {
        // Queue the data to be sent once iframe is loaded
        this.pendingDataUpdate = true;
        return;
      }

      const renderData = {
        display: {
          elements: [
            {
              type: 'poll',
              poll: {
                ...this.pollData,
                editorState: {
                  pollStage: this.element.currentDisplayState || 'results',
                  roundIndex: this.element.currentRoundIndex || 0,
                  questionIndex: this.element.currentQuestionIndex || 0,
                  animateResults: this.element.animateResults || false,
                },
              },
              settings: {
                visible: true,
                displayId: this.displayId,
              },
            },
          ],
        },
        fonts: this.pollData?.fonts ?? [],
        interactive: false,
      };

      const plainData = JSON.parse(JSON.stringify(renderData));
      this.$refs.iframeEl.contentWindow?.postMessage(
        {
          type: 'display-builder-update',
          data: plainData,
        },
        '*',
      );

      this.pendingDataUpdate = false;
    },

    updateIframeSrc() {
      // Debounce update iframeSrc,
      // so we aren't constantly reloading the frame when the URL is changing
      clearTimeout(this.updateTimeout);
      this.updateTimeout = setTimeout(() => {
        if (this.isSocialDisplay || isValidUrl(this.computedUrl)) {
          this.iframeSrc = this.computedUrl;
        }
      }, 1e3);
    },

    handleIframeReady() {
      // Called when social display iframe sends ready message
      this.ready = true;
      // Send initial settings
      this.$nextTick(() => {
        this.sendSettingsToIframe();
      });
    },

    sendSettingsToIframe() {
      if (!this.$refs.iframeEl || !this.isSocialDisplay) {
        return;
      }

      const settings = {};

      // Send postAnimation if set
      if (this.element.postAnimation) {
        settings.animation = this.element.postAnimation;
      }

      // Send other settings if available
      if (this.element.interactive !== undefined) {
        settings.interactive = this.element.interactive;
      }

      if (this.element.showCta !== undefined) {
        settings.showCtaText = this.element.showCta;
        settings.showCtaSocialIcons = this.element.showCta;
      }

      if (this.element.cta) {
        settings.ctaText = this.element.cta;
      }

      if (this.element.cycleSpeed) {
        settings.displayTime = this.element.cycleSpeed;
      }

      // Handle background - solid color or gradient
      if (this.element.fillStyle === 'gradient') {
        const direction = this.element.gradientDirection || 180;
        const colorOne = this.element.gradientColorOne || '#00c4dd';
        const colorTwo = this.element.gradientColorTwo || '#0f84fb';
        settings.backgroundColor = `linear-gradient(${direction}deg, ${colorOne}, ${colorTwo})`;
      } else if (this.element.background) {
        settings.backgroundColor = this.element.background;
      }

      this.$refs.iframeEl.contentWindow?.postMessage(
        JSON.stringify({
          type: 'tgbLive:settings',
          data: { settings },
        }),
        '*',
      );
    },
  },
};
</script>

<style lang="scss" scoped>
iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}
</style>
